import { useState } from "react";
import { ListMusic, Plus, X, ChevronRight } from "lucide-react";
import { SLEEP_PLAYLIST } from "@/SpotifyPlayer";

const KEY = "closed-eyes-playlists";
const DEFAULT = { uri: SLEEP_PLAYLIST, title: "Sleep", thumb: "", builtIn: true };

export const parseSpotify = (text) => {
  const s = text.trim();
  const uri = s.match(/^spotify:(playlist|album):([A-Za-z0-9]+)$/);
  if (uri) return `spotify:${uri[1]}:${uri[2]}`;
  const url = s.match(/open\.spotify\.com\/(?:intl-[a-z-]+\/)?(?:embed\/)?(playlist|album)\/([A-Za-z0-9]+)/);
  return url ? `spotify:${url[1]}:${url[2]}` : null;
};

const toUrl = (uri) => { const [, type, id] = uri.split(":"); return `https://open.spotify.com/${type}/${id}`; };

const fetchMeta = async (uri) => {
  try {
    const r = await fetch(`https://open.spotify.com/oembed?url=${encodeURIComponent(toUrl(uri))}`);
    if (!r.ok) return null;
    const d = await r.json();
    return { title: d.title, thumb: d.thumbnail_url };
  } catch {
    return null;
  }
};

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || [];
  } catch {
    return [];
  }
};

export const PlaylistPanel = ({ activeUri, onPlay }) => {
  const [saved, setSaved] = useState(load);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(() => window.innerWidth > 640);

  const persist = (list) => { setSaved(list); localStorage.setItem(KEY, JSON.stringify(list)); };

  const add = async (e) => {
    e.preventDefault();
    const uri = parseSpotify(text);
    if (!uri) return setError("Paste a Spotify playlist or album link");
    setError("");
    setBusy(true);
    const meta = await fetchMeta(uri);
    setBusy(false);
    if (!meta) return setError("Couldn't find that on Spotify");
    const item = { uri, ...meta };
    if (uri !== DEFAULT.uri) persist([item, ...saved.filter((p) => p.uri !== uri)]);
    setText("");
    onPlay(uri);
  };

  if (!open) {
    return (
      <button className="playlist-tab" onClick={() => setOpen(true)} data-testid="playlist-panel-toggle">
        <ListMusic size={14} /> Playlists
      </button>
    );
  }

  return (
    <aside className="playlist-panel" data-testid="playlist-panel">
      <div className="pl-head">
        <span><ListMusic size={14} /> Your playlists</span>
        <button className="pl-close" onClick={() => setOpen(false)} aria-label="Hide playlists" data-testid="playlist-panel-close"><ChevronRight size={16} /></button>
      </div>
      <form className="pl-form" onSubmit={add}>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste a Spotify playlist link"
          onKeyDown={(e) => e.stopPropagation()} data-testid="playlist-link-input" />
        <button type="submit" disabled={busy || !text.trim()} aria-label="Add playlist" data-testid="playlist-add-button"><Plus size={16} /></button>
      </form>
      {error && <p className="pl-error" data-testid="playlist-error">{error}</p>}
      <ul className="pl-list" data-testid="playlist-list">
        {[...saved, DEFAULT].map((p) => (
          <li key={p.uri} data-active={p.uri === activeUri}>
            <button className="pl-item" onClick={() => onPlay(p.uri)} data-testid={`playlist-item-${p.uri.split(":")[2]}`}>
              {p.thumb ? <img src={p.thumb} alt="" /> : <span className="pl-thumb"><ListMusic size={14} /></span>}
              <span className="pl-title">{p.title}</span>
            </button>
            {!p.builtIn && (
              <button className="pl-remove" onClick={() => persist(saved.filter((x) => x.uri !== p.uri))}
                aria-label={`Remove ${p.title}`} data-testid={`playlist-remove-${p.uri.split(":")[2]}`}><X size={13} /></button>
            )}
          </li>
        ))}
      </ul>
    </aside>
  );
};
